import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { Badge, Button, Column, DropdownMenu, DropdownMenuItem, IconButton, PageContentHeader, Typography } from 'seagull';
import { Bulb, Kebab } from 'seagull/icons';
import { routes } from '~/constants';
import { useEventReporter, useOverlayContext, useIoContext, useStore, useStoreActions } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useCopyDirectLink } from '~/hooks/useCopyDirectLink';
import { useCopyProposalLink } from '~/hooks/useCopyProposalLink';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useProposalActions } from '~/hooks/useProposalActions';
import { useSendReminder } from '~/hooks/useSendReminder';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { useWithdrawToEditProposal } from '~/hooks/useWithdrawToEditProposal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { relationshipModel } from '~/models';
import MinisiteTabHeader from '../../MinisiteTabHeader/MinisiteTabHeader';
import styles from './Header.module.scss';
const testPrefix = 'agreement-minisite-topbar-';
export default function Header(param) {
    let { relationship, onUpdate } = param;
    const { t: headerTexts } = useLocaleKeys('pages.agreementMinisite.header');
    const userInfo = useStore((state)=>state.userInfo);
    const { t: commonTexts } = useLocaleKeys('common');
    const { logBI } = useStoreActions();
    const { downloadAgreementPdf } = useDownloadAgreementPdf();
    const { reportPopupAction } = useEventReporter();
    const { withdrawToEditProposalByIdWithConfirmation } = useWithdrawToEditProposal();
    const userIsVendor = relationship.vendor.id === (userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId());
    const agreementStatusBadgeMap = {
        pending: headerTexts.statusBadges.pending(),
        terminated: headerTexts.statusBadges.terminated(),
        canceled: headerTexts.statusBadges.canceled()
    };
    const agreementStatusString = agreementStatusBadgeMap[relationship.status];
    const { terminateAgreementById } = useTerminateAgreement();
    const { copyDirectLink } = useCopyDirectLink();
    const { sendReminder } = useSendReminder();
    const overlayContext = useOverlayContext();
    const { browser: { copyToClipboard } } = useIoContext();
    const history = useHistory();
    const { getAgreementNotificationLink } = useCopyProposalLink();
    const { getCancelProposalAction } = useProposalActions();
    const isMobileDevice = useIsMobileForHomePage();
    const overflowMenuActions = compact([
        relationshipModel.isPending(relationship) && userIsVendor && {
            intent: Intent.SendProposalReminder,
            text: headerTexts.resendLinkToCustomer(),
            testId: 'resend',
            bi: {
                name: 'resend_link_to_customer'
            },
            onClick: ()=>sendReminder(relationship)
        },
        relationshipModel.isPending(relationship) && userIsVendor && {
            intent: Intent.CopyRelationshipDirectLink,
            text: headerTexts.copyAgreementLink(),
            testId: 'copy',
            bi: {
                name: 'copy_agreement_link'
            },
            onClick: async ()=>{
                const { vendor, customer, progressStatus } = relationship;
                logBI(10586, {
                    vendorId: vendor.id,
                    clientId: customer.id,
                    agreementStatus: progressStatus
                });
                overlayContext.startLoader();
                const { success, data } = await handlePromise(getAgreementNotificationLink(relationship.id));
                overlayContext.stopLoader();
                if (success) {
                    try {
                        // This will fail if the document loses focus.
                        copyToClipboard(data);
                        overlayContext.showSuccessToast(commonTexts.toasts.linkCopiedToClipboard());
                    } catch (err) {
                        overlayContext.showErrorToast(commonTexts.toasts.failedToCopyLinkToClipboard());
                    }
                }
            }
        },
        relationshipModel.isPending(relationship) && userIsVendor && _object_spread_props(_object_spread({}, getCancelProposalAction(relationship.id, async ()=>history.push(routes.home))), {
            testId: 'cancel',
            bi: {
                name: 'cancel_proposal'
            }
        }),
        userIsVendor && {
            intent: Intent.CopyRelationshipDirectLink,
            text: headerTexts.copyLink(),
            testId: 'copy',
            bi: {
                name: 'copy_link'
            },
            onClick: ()=>{
                copyDirectLink(relationship.id, {
                    type: 'agreement',
                    id: relationship.id
                });
                logBI(14000);
            }
        },
        (relationshipModel.isApproved(relationship) || relationshipModel.isTerminated(relationship)) && {
            intent: Intent.DownloadAgreementPdf,
            text: headerTexts.downloadPdf(),
            testId: 'download-pdf',
            bi: {
                name: 'download_pdf'
            },
            onClick: ()=>downloadAgreementPdf(relationship.id)
        },
        relationshipModel.isApproved(relationship) && userIsVendor && {
            intent: Intent.TerminateAgreement,
            text: headerTexts.terminateAgreement(),
            testId: 'terminate',
            bi: {
                name: 'terminate_agreement'
            },
            onClick: ()=>{
                reportPopupAction('terminate-agreement-modal');
                terminateAgreementById({
                    id: relationship.id,
                    postAction: ()=>onUpdate()
                });
            }
        }
    ]);
    const showEditButton = userIsVendor && !relationshipModel.isTerminated(relationship);
    const editButton = showEditButton && /*#__PURE__*/ _jsx(Button, {
        bi: {
            name: 'edit_agreement'
        },
        intent: Intent.EditProposal,
        fluid: isMobileDevice,
        onClick: ()=>{
            if (relationshipModel.isPending(relationship)) {
                if (relationship.draftId == null) return;
                withdrawToEditProposalByIdWithConfirmation(relationship.id, relationship.draftId);
            } else {
                history.push(routes.editAgreement(relationship.id));
            }
        },
        testId: testPrefix + 'btn-edit-sow',
        disabled: relationship.isSample || relationship.withdrawnForEditing,
        children: relationship.withdrawnForEditing ? headerTexts.editing() : headerTexts.edit()
    });
    const overflowDropdown = overflowMenuActions.length > 0 && /*#__PURE__*/ _jsx(DropdownMenu, {
        target: isMobileDevice ? /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'open_agreement_overflow_menu'
            },
            skin: "primaryOutline",
            fluid: true,
            testId: testPrefix + 'vendor-overflow-button',
            children: headerTexts.moreActions()
        }) : /*#__PURE__*/ _jsx(IconButton, {
            bi: {
                name: 'open_agreement_overflow_menu'
            },
            testId: testPrefix + 'vendor-overflow-button',
            children: /*#__PURE__*/ _jsx(Kebab, {})
        }),
        children: overflowMenuActions.map((param)=>{
            let { intent, text, testId, bi, onClick } = param;
            return /*#__PURE__*/ _jsx(DropdownMenuItem, {
                intent: intent,
                testId: "".concat(testPrefix, "overflow-").concat(testId),
                bi: bi,
                onClick: onClick,
                children: text
            }, text);
        })
    });
    const showMobileBottomBar = isMobileDevice && (showEditButton || overflowMenuActions.length > 0);
    const statusBadge = agreementStatusString && /*#__PURE__*/ _jsx(Badge, {
        size: "big",
        skin: "warning",
        testId: testPrefix + 'badge-status',
        "data-status": relationship.status,
        children: agreementStatusString
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            (!isMobileDevice || statusBadge) && /*#__PURE__*/ _jsx(MinisiteTabHeader, {
                appcuesId: "agreement-view-header",
                actions: /*#__PURE__*/ _jsxs("div", {
                    className: styles.actions,
                    "data-appcuesid": "agreement-view-actions",
                    children: [
                        statusBadge,
                        !isMobileDevice && editButton,
                        !isMobileDevice && overflowDropdown
                    ]
                })
            }),
            showMobileBottomBar && /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(Column, {
                gap: 8,
                className: styles.mobileBottomBar,
                children: [
                    showEditButton && editButton,
                    overflowDropdown
                ]
            }), document.body),
            relationship.isSample && /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    "data-testid": testPrefix + 'sample-banner',
                    className: styles.sampleAgreementBanner,
                    children: [
                        /*#__PURE__*/ _jsx(Bulb, {}),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "light",
                            className: styles.sampleAgreementBannerText,
                            children: headerTexts.sampleAgreementBanner()
                        })
                    ]
                })
            })
        ]
    });
}
